
import { Vue, Component } from 'vue-property-decorator';
import { createMatch, updateMatch, getMatchDetail } from '@/api/match';

@Component
export default class MatchCreate extends Vue {
  private calYear = new Date().getFullYear()

  private calMonth = new Date().getMonth()

  private selectedDate = ''

  private selectedHour = '20'

  private selectedMinute = '00'

  private matchDuration = 2

  private stadium = ''

  private matchType = 'FRIENDLY'

  private matchFormat = 'FIVE_VS_FIVE'

  private fee = 0

  private durationOptions = [1, 2, 3, 4]

  private feePresets = [10000, 20000, 30000, 50000]

  private matchRules = ''

  private formatOptions = [
    { label: '4 vs 4', value: 'FOUR_VS_FOUR' },
    { label: '5 vs 5', value: 'FIVE_VS_FIVE' },
    { label: '6 vs 6', value: 'SIX_VS_SIX' },
    { label: '7 vs 7', value: 'SEVEN_VS_SEVEN' },
  ]

  get teamUid(): string {
    return (this.$route.query.teamUid as string) || '';
  }

  get matchUid(): string {
    return (this.$route.query.matchUid as string) || '';
  }

  get isEditMode(): boolean {
    return !!this.matchUid;
  }

  async created(): Promise<void> {
    if (this.isEditMode) {
      try {
        const res = await getMatchDetail(this.matchUid);
        const m = res.data;
        if (m) {
          this.selectedDate = m.matchDate || '';
          if (m.matchDate) {
            const d = new Date(m.matchDate);
            this.calYear = d.getFullYear();
            this.calMonth = d.getMonth();
          }
          if (m.matchTime) {
            const parts = String(m.matchTime).split(':');
            this.selectedHour = parts[0] || '20';
            this.selectedMinute = parts[1] || '00';
          }
          this.matchDuration = m.durationHours || 2;
          this.stadium = m.stadiumName || '';
          this.matchType = m.matchType || 'FRIENDLY';
          const formatNormalize: Record<string, string> = {
            '4vs4': 'FOUR_VS_FOUR',
            '5vs5': 'FIVE_VS_FIVE',
            '6vs6': 'SIX_VS_SIX',
            '7vs7': 'SEVEN_VS_SEVEN',
          };
          const rawFormat = m.matchFormat || '';
          this.matchFormat = formatNormalize[rawFormat] || rawFormat || 'FIVE_VS_FIVE';
          this.fee = m.fee || 0;
          this.matchRules = m.matchRules || '';
        }
      } catch (e) {
        this.$message.error('매치 정보를 불러오지 못했습니다.');
      }
    }
  }

  get hours(): string[] {
    const h: string[] = [];
    for (let i = 0; i < 24; i += 1) {
      h.push(String(i).padStart(2, '0'));
    }
    return h;
  }

  get minutes(): string[] {
    return ['00', '10', '20', '30', '40', '50'];
  }

  get displayDateTime(): string {
    if (!this.selectedDate) return '날짜를 선택해주세요';
    const d = new Date(this.selectedDate);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}년 ${m}월 ${day}일 ${this.selectedHour}시 ${this.selectedMinute}분`;
  }

  get calendarDays(): any[] {
    const days: any[] = [];
    const firstDay = new Date(this.calYear, this.calMonth, 1);
    const lastDay = new Date(this.calYear, this.calMonth + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 이전 달
    const startDow = firstDay.getDay();
    const prevLast = new Date(this.calYear, this.calMonth, 0).getDate();
    for (let i = startDow - 1; i >= 0; i -= 1) {
      days.push({
        day: prevLast - i,
        current: false,
        isPast: true,
        isToday: false,
        isSelected: false,
        dateStr: '',
      });
    }

    // 현재 달
    for (let d = 1; d <= lastDay.getDate(); d += 1) {
      const dt = new Date(this.calYear, this.calMonth, d);
      const dateStr = `${this.calYear}-${String(this.calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({
        day: d,
        current: true,
        isPast: dt < today,
        isToday: dt.getTime() === today.getTime(),
        isSelected: this.selectedDate === dateStr,
        dateStr,
      });
    }

    // 다음 달
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i += 1) {
      days.push({
        day: i,
        current: false,
        isPast: false,
        isToday: false,
        isSelected: false,
        dateStr: '',
      });
    }

    return days;
  }

  private prevMonth(): void {
    if (this.calMonth === 0) {
      this.calMonth = 11;
      this.calYear -= 1;
    } else {
      this.calMonth -= 1;
    }
  }

  private nextMonth(): void {
    if (this.calMonth === 11) {
      this.calMonth = 0;
      this.calYear += 1;
    } else {
      this.calMonth += 1;
    }
  }

  private selectDate(day: any): void {
    if (!day.current || day.isPast) return;
    this.selectedDate = day.dateStr;
  }

  private isWithinSevenDays(): boolean {
    if (!this.selectedDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(this.selectedDate);
    selectedDate.setHours(0, 0, 0, 0);
    const diffTime = selectedDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  }

  private async handleSubmit(): Promise<void> {
    if (!this.selectedDate) {
      this.$message.warning('날짜를 선택해주세요.');
      return;
    }
    if (!this.stadium) {
      this.$message.warning('장소를 입력해주세요.');
      return;
    }

    try {
      const matchData: any = {
        hostTeamUid: this.teamUid,
        matchDate: this.selectedDate,
        matchTime: `${this.selectedHour}:${this.selectedMinute}`,
        durationHours: this.matchDuration,
        stadiumName: this.stadium,
        matchType: this.matchType,
        matchFormat: this.matchFormat,
        fee: this.fee,
        matchRules: this.matchRules,
      };

      if (this.isEditMode) {
        await updateMatch(this.matchUid, {
          matchDate: matchData.matchDate,
          matchTime: matchData.matchTime,
          durationHours: matchData.durationHours,
          stadiumName: matchData.stadiumName,
          matchFormat: matchData.matchFormat,
          fee: matchData.fee,
          matchRules: matchData.matchRules,
        });
        this.$message.success('매치가 수정되었습니다.');
        this.$router.go(-1);
      } else {
        const response = await createMatch(matchData);
        this.$message.success('매치 일정이 등록되었습니다!');

        // 경기 날짜가 7일 이내인지 확인
        if (this.isWithinSevenDays()) {
          // 게스트 모집 여부 묻기
          this.$confirm('게스트 모집도 함께 하시겠습니까?', '게스트 모집', {
            confirmButtonText: '게스트 모집하기',
            cancelButtonText: '나중에',
            type: 'info',
          }).then(() => {
            this.$router.push({
              path: '/guest-recruit',
              query: {
                teamUid: this.teamUid,
                matchUid: response.data?.uid || '',
                matchDate: this.selectedDate,
                matchTime: `${this.selectedHour}:${this.selectedMinute}`,
                stadium: this.stadium,
              },
            });
          }).catch(() => {
            this.$router.go(-1);
          });
        } else {
          // 7일 이후 경기는 바로 완료
          this.$router.go(-1);
        }
      }
    } catch (error) {
      console.error('Failed to save match:', error);
      this.$message.error(this.isEditMode ? '매치 수정에 실패했습니다.' : '매치 일정 등록에 실패했습니다.');
    }
  }
}
